/**
 * Client workspace · Creative Intelligence — workflow page (Task 225, Milestone A).
 *
 * Mounted at `/app/clients/:clientId/creative-intelligence` (Task 223); `clientId`
 * comes from the route (`useParams`) — no client picker, no org-wide client fetch.
 *
 * Task 225 (Milestone A) reorganizes this surface from a flat report into an
 * explicit, sequenced WORKFLOW:
 *   Stage 1 — paid-media performance / cross-platform content groups (Task 221).
 *   Stage 2 — asset matching: pick a creative asset, review suggested paid-media
 *             matches, manually link (Task 222 B1).
 *   Stage 3 — creative-content analysis — placeholder, "not yet run" (the analysis
 *             layer ships in later milestones B–D).
 *   Stage 4 — strategy selection (the Task 224 deterministic board) — LOCKED until
 *             creative analysis exists; strategy must come AFTER analysis results,
 *             not after performance alone.
 *
 * FE-only: no AI, no persistence, no content-batch creation, no approvals. The
 * only write is the pre-existing Task 222 B1 manual link.
 */

import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  completeCiAssetUploads,
  createCiAssetUploads,
  createCreativeAssetMatch,
  getCreativeAnalysisStatus,
  listClientStrategies,
  listCreativeAssets,
  listCreativeIntelligenceContentGroups,
  putToSignedUploadUrl,
  suggestCreativeAssetPaidMatches,
} from '../../api/endpoints';
import {
  summarizeCreativeAnalysisStatus,
  type CreativeAnalysisStatusSummary,
} from './creativeAnalysisStatus';
import { useMeContext } from '../../context/MeContextProvider';
import { hasAnyPermission, isLeadership, PERM } from '../../permissions';
import { StrategyRulesPanel } from './StrategyRulesPanel';
import {
  buildStrategyCandidates,
  type CreativeStrategyCandidate,
  type CreativeStrategyConfidence,
  type CreativeStrategyKind,
} from './creativeStrategy';
import { CREATIVE_ANALYSIS_DISABLED_MESSAGE } from './creativeAnalysisGate';
import {
  CREATIVE_BULK_UPLOAD_ENABLED,
  CREATIVE_WORKFLOW_STAGES,
  creativeWorkflowStageState,
  normalizeCreativeFilename,
  type CreativeWorkflowGateInputs,
  type CreativeWorkflowStageId,
} from './creativeWorkflowStages';
import type {
  CiAssetUploadKind,
  ClientStrategyRow,
  CompleteCiAssetUploadsItem,
  CreativeAssetMatchMethod,
  CreativeAssetPaidMatchMethod,
  CreativeAssetPaidMatchPlatformConnection,
  CreativeAssetRow,
  CreativeIntelligenceContentGroup,
  CreativeIntelligenceFatigueSignal,
  CreativeIntelligencePerformanceSignal,
  CreativeIntelligencePlatform,
  CreativeIntelligencePlatformAvailability,
  CreativeIntelligenceRecommendationKind,
  SuggestCreativeAssetPaidMatchesData,
} from '../../api/types';
import { EmptyState, ErrorState, LoadingState } from '../../components/states';

const PLATFORM_LABEL: Record<CreativeIntelligencePlatform, string> = {
  meta_ads: 'Meta',
  snapchat_ads: 'Snapchat',
  tiktok_ads: 'TikTok',
};
const AVAILABILITY_LABEL: Record<CreativeIntelligencePlatformAvailability, string> = {
  has_data: 'بيانات متاحة',
  no_data: 'لا توجد بيانات',
  not_connected: 'غير مربوط',
  pending_activation: 'بانتظار التفعيل',
};
const CONNECTION_LABEL: Record<CreativeAssetPaidMatchPlatformConnection, string> = {
  connected: 'مربوط',
  not_connected: 'غير مربوط',
  pending_activation: 'بانتظار التفعيل',
};
const PAID_METHOD_LABEL: Record<CreativeAssetPaidMatchMethod, string> = {
  exact_name: 'اسم مطابق',
  alias: 'اسم بديل',
  fuzzy: 'تقارب',
};
const MATCH_METHOD_LABEL: Record<CreativeAssetMatchMethod, string> = {
  exact_name: 'اسم مطابق',
  alias: 'اسم بديل',
  fuzzy: 'تقارب',
  manual_link: 'ربط يدوي',
};
const PERF_LABEL: Record<CreativeIntelligencePerformanceSignal, string> = {
  winner: 'رابح',
  stable: 'مستقر',
  weak: 'ضعيف',
  waste: 'هدر',
};
const FATIGUE_LABEL: Record<CreativeIntelligenceFatigueSignal, string> = {
  none: '—',
  watch: 'مراقبة',
  fatigued: 'إجهاد',
};
const RECO_LABEL: Record<CreativeIntelligenceRecommendationKind, string> = {
  scale_winner: 'وسّع الرابح',
  refresh_fatigued: 'جدّد الإبداع',
  adapt_platform: 'كيّف حسب المنصة',
  stop_waste: 'أوقف الهدر',
  needs_more_data: 'بيانات غير كافية',
};

// Task 224 — strategy decision board labels (deterministic, no AI).
const STRATEGY_KIND_LABEL: Record<CreativeStrategyKind, string> = {
  scale_winner: 'وسّع الرابح',
  stop_waste: 'أوقف الهدر',
  refresh_fatigued: 'جدّد الإبداع المُجهَد',
  adapt_platform: 'كيّف الرابح لمنصة أضعف',
  fix_matching: 'أصلح المطابقة — اربط الإبداع أولاً',
  needs_more_data: 'بيانات غير كافية',
};
const STRATEGY_NEXT_MOVE: Record<CreativeStrategyKind, string> = {
  scale_winner: 'ارفع الميزانية تدريجياً على المنصات الرابحة وراقب ثبات ROAS.',
  stop_waste: 'أوقف أو قلّص الإنفاق على المنصات المهدِرة وأعد توجيه الميزانية.',
  refresh_fatigued: 'حدّث الإبداع (زاوية/خطاف/تصميم جديد) قبل أن يزداد الإجهاد.',
  adapt_platform: 'أعد تكييف الإبداع الرابح ليناسب المنصة المتعثّرة (مقاس/رسالة).',
  fix_matching: 'اربط الإعلانات بالأصل الإبداعي الصحيح لتحليل أدق على مستوى المحتوى.',
  needs_more_data: 'اترك الحملة تجمع المزيد من الظهور قبل اتخاذ قرار.',
};
const STRATEGY_CONFIDENCE_LABEL: Record<CreativeStrategyConfidence, string> = {
  high: 'ثقة عالية',
  medium: 'ثقة متوسطة',
  low: 'ثقة منخفضة',
};

function isoDate(d: Date): string {
  return d.toISOString().slice(0, 10);
}
function fmtNum(n: number): string {
  return new Intl.NumberFormat('en-US').format(Math.round(n));
}
function fmtMoney(n: number): string {
  return `${new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(n)} SAR`;
}
function fmtPct(n: number | null): string {
  return n === null ? '—' : `${(n * 100).toFixed(2)}%`;
}
function fmtRatio(n: number | null): string {
  return n === null ? '—' : n.toFixed(2);
}
function fmtConfidence(n: number | null): string {
  return n === null ? '—' : `${Math.round(n * 100)}%`;
}

type GroupsState =
  | { readonly status: 'idle' }
  | { readonly status: 'loading' }
  | {
      readonly status: 'ready';
      readonly groups: ReadonlyArray<CreativeIntelligenceContentGroup>;
      readonly partial: boolean;
    }
  | { readonly status: 'error'; readonly error: unknown };

type AssetsState =
  | { readonly status: 'idle' }
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly assets: ReadonlyArray<CreativeAssetRow> }
  | { readonly status: 'error'; readonly error: unknown };

// Task 259 — the client's ACTIVE strategies (with an active version), offered
// by the stage-5 selector before deriving Strategy Rules.
type ClientStrategiesState =
  | { readonly status: 'idle' }
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly strategies: ReadonlyArray<ClientStrategyRow> }
  | { readonly status: 'error' };

type SuggestState =
  | { readonly status: 'idle' }
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly data: SuggestCreativeAssetPaidMatchesData }
  | { readonly status: 'error'; readonly error: unknown };

// Task 260 — REAL creative-analysis status, loaded from the Task 227
// `get-creative-analysis-status` EF (client-scoped; jobs + persisted analyses,
// RLS-gated server-side). Replaces the Milestone-A placeholder that
// hard-returned 'not_run' even while real analyses existed in production.
type AnalysisStatusState =
  | { readonly status: 'idle' }
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly summary: CreativeAnalysisStatusSummary }
  | { readonly status: 'error' };

export function CreativeIntelligencePage() {
  // Client context comes from the route — NOT a client picker. The
  // ClientWorkspaceLayout + ClientScopeGate establish the workspace.
  const { clientId } = useParams();
  const today = new Date();
  const [dateFrom, setDateFrom] = useState<string>(isoDate(new Date(today.getTime() - 30 * 864e5)));
  const [dateTo, setDateTo] = useState<string>(isoDate(today));
  const [groupsState, setGroupsState] = useState<GroupsState>({ status: 'idle' });

  // Task 222 B1 — asset selection + match review.
  const [assetsState, setAssetsState] = useState<AssetsState>({ status: 'idle' });
  const [assetId, setAssetId] = useState<string | null>(null);
  const [suggestState, setSuggestState] = useState<SuggestState>({ status: 'idle' });
  const [ignored, setIgnored] = useState<ReadonlySet<string>>(new Set());
  const [linkingAdId, setLinkingAdId] = useState<string | null>(null);
  const [linkError, setLinkError] = useState<string | null>(null);
  // Bumped after a successful link to refetch suggestions AND content groups.
  const [reloadToken, setReloadToken] = useState(0);
  // Task 237 — bumped after a successful CI bulk upload to refetch this client's assets.
  const [assetReloadToken, setAssetReloadToken] = useState(0);

  // Task 235 — staged workflow: which stage is currently visible (one at a time).
  const [activeStage, setActiveStage] = useState<CreativeWorkflowStageId>('performance');

  // Task 224 — deterministic strategy decision board, derived purely from the
  // loaded content groups (no AI, no writes). Selection is UI-local; a stale
  // selection simply resolves to no summary once its candidate disappears.
  const [selectedStrategyId, setSelectedStrategyId] = useState<string | null>(null);
  const strategyCandidates = useMemo<ReadonlyArray<CreativeStrategyCandidate>>(
    () => (groupsState.status === 'ready' ? buildStrategyCandidates(groupsState.groups) : []),
    [groupsState],
  );

  // Task 259 — Strategy Rules live HERE (the workflow owns derive/review):
  // after Analysis, the user selects an existing ACTIVE client strategy (with
  // an active version) and derives rules from the analysis + performance
  // evidence. Same permission model as before: view = strategy.rule.review
  // (media_buyer included); derive/approve/dismiss = leadership only. The
  // backend re-checks everything.
  const { me } = useMeContext();
  const canViewStrategyRules =
    !!me && !me.system_admin && hasAnyPermission(me.permissions, PERM.STRATEGY_RULE_REVIEW);
  const canManageStrategyRules = !!me && !me.system_admin && isLeadership(me);
  const [clientStrategiesState, setClientStrategiesState] = useState<ClientStrategiesState>({
    status: 'idle',
  });
  const [rulesStrategyId, setRulesStrategyId] = useState<string | null>(null);

  // Load the client's strategies once the strategy stage is reachable; the
  // selector offers ACTIVE strategies that have an active version.
  useEffect(() => {
    if (!clientId || !canViewStrategyRules) {
      setClientStrategiesState({ status: 'idle' });
      return;
    }
    const ctrl = new AbortController();
    setClientStrategiesState({ status: 'loading' });
    listClientStrategies(clientId, { signal: ctrl.signal })
      .then((data) => {
        if (ctrl.signal.aborted) return;
        const eligible = data.strategies.filter((s) => s.is_active && s.active_version_id !== null);
        setClientStrategiesState({ status: 'ready', strategies: eligible });
        setRulesStrategyId((prev) =>
          prev !== null && eligible.some((s) => s.id === prev) ? prev : (eligible[0]?.id ?? null),
        );
      })
      .catch((err: unknown) => {
        if (err instanceof DOMException && err.name === 'AbortError') return;
        setClientStrategiesState({ status: 'error' });
      });
    return () => {
      ctrl.abort();
    };
  }, [clientId, canViewStrategyRules]);

  // Load content groups for this client + window (+ reload after a link).
  useEffect(() => {
    if (!clientId) {
      setGroupsState({ status: 'idle' });
      return;
    }
    const ctrl = new AbortController();
    setGroupsState({ status: 'loading' });
    listCreativeIntelligenceContentGroups({ clientId, dateFrom, dateTo, signal: ctrl.signal })
      .then((data) => {
        if (ctrl.signal.aborted) return;
        setGroupsState({ status: 'ready', groups: data.content_groups, partial: data.partial });
      })
      .catch((err: unknown) => {
        if (ctrl.signal.aborted || (err instanceof DOMException && err.name === 'AbortError'))
          return;
        setGroupsState({ status: 'error', error: err });
      });
    return () => ctrl.abort();
  }, [clientId, dateFrom, dateTo, reloadToken]);

  // Load this client's creative assets for the selector; reset selection when
  // the route's client changes (React Router reuses this component).
  useEffect(() => {
    setAssetId(null);
    setIgnored(new Set());
    setLinkError(null);
    if (!clientId) {
      setAssetsState({ status: 'idle' });
      return;
    }
    const ctrl = new AbortController();
    setAssetsState({ status: 'loading' });
    listCreativeAssets(clientId, { signal: ctrl.signal })
      .then((data) => {
        if (ctrl.signal.aborted) return;
        setAssetsState({ status: 'ready', assets: data.assets });
      })
      .catch((err: unknown) => {
        if (ctrl.signal.aborted || (err instanceof DOMException && err.name === 'AbortError'))
          return;
        setAssetsState({ status: 'error', error: err });
      });
    return () => ctrl.abort();
  }, [clientId, assetReloadToken]);

  // Load suggested matches for the selected asset (+ reload after a link).
  useEffect(() => {
    if (!clientId || !assetId) {
      setSuggestState({ status: 'idle' });
      return;
    }
    const ctrl = new AbortController();
    setSuggestState({ status: 'loading' });
    suggestCreativeAssetPaidMatches({ assetId, clientId, dateFrom, dateTo, signal: ctrl.signal })
      .then((data) => {
        if (ctrl.signal.aborted) return;
        setSuggestState({ status: 'ready', data });
      })
      .catch((err: unknown) => {
        if (ctrl.signal.aborted || (err instanceof DOMException && err.name === 'AbortError'))
          return;
        setSuggestState({ status: 'error', error: err });
      });
    return () => ctrl.abort();
  }, [clientId, assetId, dateFrom, dateTo, reloadToken]);

  function handleLink(adId: string) {
    if (!assetId || linkingAdId !== null) return;
    setLinkingAdId(adId);
    setLinkError(null);
    createCreativeAssetMatch({ asset_id: assetId, ad_id: adId })
      .then(() => {
        // Refresh suggestions + content groups so the linked ad regroups.
        setReloadToken((n) => n + 1);
      })
      .catch((err: unknown) => {
        if (err instanceof DOMException && err.name === 'AbortError') return;
        setLinkError('تعذّر إنشاء الربط — تأكد من الصلاحية وعدم وجود ربط نشط مكرر لنفس الإعلان.');
      })
      .finally(() => setLinkingAdId(null));
  }

  function handleIgnore(adId: string) {
    setIgnored((prev) => {
      const next = new Set(prev);
      next.add(adId);
      return next;
    });
  }

  // Task 237 — after a CI bulk upload lands, refetch this client's assets (the
  // match-review picker) AND the content groups. Upload creates NO match —
  // matching stays the Task 222 manual flow in the Match Review stage.
  function handleUploaded() {
    setAssetReloadToken((n) => n + 1);
    setReloadToken((n) => n + 1);
  }

  // Task 260 — real analysis status: load jobs + persisted analyses for this
  // client and derive the stage-4 facts (counts / failed / in-flight / latest).
  // `analysisReady` is now TRUE exactly when real analyses exist.
  const [analysisState, setAnalysisState] = useState<AnalysisStatusState>({ status: 'idle' });
  useEffect(() => {
    if (!clientId) {
      setAnalysisState({ status: 'idle' });
      return;
    }
    const ctrl = new AbortController();
    setAnalysisState({ status: 'loading' });
    getCreativeAnalysisStatus(clientId, { signal: ctrl.signal })
      .then((data) => {
        if (ctrl.signal.aborted) return;
        setAnalysisState({
          status: 'ready',
          summary: summarizeCreativeAnalysisStatus(data.jobs, data.analyses),
        });
      })
      .catch((err: unknown) => {
        if (ctrl.signal.aborted || (err instanceof DOMException && err.name === 'AbortError'))
          return;
        setAnalysisState({ status: 'error' });
      });
    return () => {
      ctrl.abort();
    };
  }, [clientId]);
  const analysisSummary = analysisState.status === 'ready' ? analysisState.summary : null;
  const analysisReady = analysisSummary !== null && analysisSummary.ready;
  const [analyzeNotice, setAnalyzeNotice] = useState<string | null>(null);

  // Activation gate (Task 230): the Analyze action is clickable but, while
  // creative analysis is disabled, it shows the friendly message and makes NO
  // backend call (no job, no cost). The live enqueue wiring lands with the
  // analysis UI (Milestone D), after the gated EF is deployed and C2 exists.
  function handleRunAnalysis() {
    setAnalyzeNotice(CREATIVE_ANALYSIS_DISABLED_MESSAGE);
  }

  // Analysis unlocks only once at least one creative asset is actually matched
  // (a content group resolved to an asset). Strategy unlocks after analysis.
  const hasMatchedAssets =
    groupsState.status === 'ready' &&
    groupsState.groups.some((g) => g.match_quality === 'asset_match');
  const stageGate: CreativeWorkflowGateInputs = {
    hasMatchedAssets,
    analysisReady,
    uploadEnabled: CREATIVE_BULK_UPLOAD_ENABLED,
  };

  if (!clientId) return null;

  return (
    <section className="page" dir="rtl">
      <header className="page-header">
        <h1>ذكاء الإبداع — مسار العمل</h1>
        <p className="page-subtitle">
          مسار متسلسل: أداء المنصات ← رفع الإبداعات ← مطابقة الأصول ← تحليل الإبداع ← اختيار
          الاستراتيجية. كل مرحلة تُبنى على ما قبلها: التحليل لا يبدأ قبل مطابقة الأصول،
          والاستراتيجية لا تظهر قبل توفّر تحليل الإبداع.
        </p>
      </header>

      <WorkflowStepper activeStage={activeStage} onSelect={setActiveStage} gate={stageGate} />

      <div className="ci-filters">
        <label>
          من
          <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
        </label>
        <label>
          إلى
          <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
        </label>
      </div>

      {/* Stage 1 — paid-media performance / cross-platform content groups (Task 221). */}
      {activeStage === 'performance' ? (
        <section className="ci-stage" aria-labelledby="ci-stage-1">
          <h2 className="ci-stage-title" id="ci-stage-1">
            <span className="ci-stage-num">1</span> الأداء عبر المنصات
          </h2>
          {groupsState.status === 'loading' ? <LoadingState /> : null}
          {groupsState.status === 'error' ? <ErrorState error={groupsState.error} /> : null}
          {groupsState.status === 'ready' && groupsState.groups.length === 0 ? (
            <EmptyState title="لا توجد مجموعات محتوى في هذه الفترة." />
          ) : null}
          {groupsState.status === 'ready' && groupsState.partial ? (
            <p className="ci-partial">
              عرض جزئي — تم بلوغ حد القراءة؛ ضيّق الفترة لرؤية كل البيانات.
            </p>
          ) : null}
          {groupsState.status === 'ready' && groupsState.groups.length > 0 ? (
            <div className="ci-groups">
              {groupsState.groups.map((g) => (
                <ContentGroupCard key={g.id} group={g} />
              ))}
            </div>
          ) : null}
        </section>
      ) : null}

      {/* Stage 2 — Upload Creatives (Task 235 + 237). First-class stage, now LIVE:
          bulk create → PUT(signed) → complete via the Task 236 EFs. No match is
          created at upload — matching stays the Task 222 flow in stage 3. */}
      {activeStage === 'upload' ? (
        <UploadCreativesStage
          clientId={clientId}
          onUploaded={handleUploaded}
          onGoToMatch={() => setActiveStage('match')}
        />
      ) : null}

      {/* Stage 3 — Match Review (Task 222 B1): pick an asset, review matched /
          suggested / unmatched ads, manually link. Works on existing assets. */}
      {activeStage === 'match' ? (
        <section className="ci-stage" aria-labelledby="ci-stage-3">
          <h2 className="ci-stage-title" id="ci-stage-3">
            <span className="ci-stage-num">3</span> مطابقة الأصول
          </h2>
          <p className="page-subtitle">
            راجِع مطابقات كل أصل: مرتبطة، مقترحة، وغير مطابقة. بعد تفعيل رفع الإبداعات ستظهر هنا
            أيضاً الأصول المرفوعة غير المطابقة لربطها يدوياً.
          </p>
          <label className="ci-asset-picker">
            الأصل الإبداعي
            {assetsState.status === 'ready' ? (
              <select
                value={assetId ?? ''}
                onChange={(e) => {
                  setAssetId(e.target.value || null);
                  setIgnored(new Set());
                  setLinkError(null);
                }}
              >
                <option value="">— اختر أصلاً للمراجعة —</option>
                {assetsState.assets.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.canonical_name}
                  </option>
                ))}
              </select>
            ) : assetsState.status === 'loading' ? (
              <span>…</span>
            ) : (
              <span>—</span>
            )}
          </label>
          {assetsState.status === 'error' ? <ErrorState error={assetsState.error} /> : null}
          {assetId ? (
            <div className="ci-asset-review">
              {linkError ? (
                <p className="ci-link-error" role="alert">
                  {linkError}
                </p>
              ) : null}
              {suggestState.status === 'loading' ? <LoadingState /> : null}
              {suggestState.status === 'error' ? <ErrorState error={suggestState.error} /> : null}
              {suggestState.status === 'ready' ? (
                <AssetMatchReview
                  data={suggestState.data}
                  ignored={ignored}
                  linkingAdId={linkingAdId}
                  onLink={handleLink}
                  onIgnore={handleIgnore}
                />
              ) : null}
            </div>
          ) : (
            <EmptyState
              title="اختر أصلاً إبداعياً لمراجعة مطابقاته."
              description="ستظهر الإعلانات المرتبطة، والمطابقات المقترحة، والإعلانات غير المطابقة."
            />
          )}
        </section>
      ) : null}

      {/* Stage 4 — creative-content analysis. Task 260: REAL status from the
          get-creative-analysis-status EF. Order matters: existing analyses
          render FIRST (results that exist are never hidden behind the match
          lock — prod has analyses while the matches table is still empty);
          the lock applies only to the not-yet-run path. The Task 230
          activation-gate button is unchanged (no AI call, no job). */}
      {activeStage === 'analysis' ? (
        <section className="ci-stage" aria-labelledby="ci-stage-4">
          <h2 className="ci-stage-title" id="ci-stage-4">
            <span className="ci-stage-num">4</span> تحليل الإبداع
          </h2>
          {analysisState.status === 'loading' || analysisState.status === 'idle' ? (
            <p className="muted" style={{ margin: 0 }}>
              جاري تحميل حالة التحليل…
            </p>
          ) : analysisState.status === 'error' ? (
            <p className="tiny" role="alert" style={{ color: 'var(--rose)', margin: 0 }}>
              تعذّر تحميل حالة تحليل الإبداع.
            </p>
          ) : analysisSummary !== null && analysisSummary.ready ? (
            <div className="ci-analysis-gate">
              <div className="card" style={{ padding: 14 }}>
                <strong>التحليل متوفر</strong>
                <p className="tiny" style={{ margin: '6px 0 0' }}>
                  التحليلات: {analysisSummary.analysesCount}
                  {' · '}الأصول المحلّلة: {analysisSummary.analyzedAssetsCount}
                  {analysisSummary.latestAnalyzedAt
                    ? ` · آخر تحليل: ${new Date(analysisSummary.latestAnalyzedAt).toLocaleDateString('ar-SA')}`
                    : ''}
                </p>
                {analysisSummary.openJobsCount > 0 ? (
                  <p className="tiny muted" style={{ margin: '4px 0 0' }}>
                    قيد المعالجة الآن: {analysisSummary.openJobsCount}
                  </p>
                ) : null}
                {analysisSummary.failedJobsCount > 0 ? (
                  <p className="tiny" style={{ color: 'var(--rose)', margin: '4px 0 0' }}>
                    محاولات فاشلة سابقة: {analysisSummary.failedJobsCount}
                  </p>
                ) : null}
              </div>
              <button type="button" className="ci-analyze-btn" onClick={handleRunAnalysis}>
                تشغيل التحليل
              </button>
              {analyzeNotice ? (
                <p className="ci-analyze-notice" role="status">
                  {analyzeNotice}
                </p>
              ) : null}
            </div>
          ) : !hasMatchedAssets ? (
            <EmptyState
              title="مقفل — أكمل مطابقة الأصول أولاً."
              description="يُفتح تحليل الإبداع بعد ربط أصل إبداعي واحد على الأقل بإعلاناته في مرحلة المطابقة."
            />
          ) : (
            <div className="ci-analysis-gate">
              <EmptyState
                title="لم يُشغّل تحليل الإبداع بعد."
                description="سيعرض هذا القسم حالة تحليل محتوى كل إبداع (الخطاف / النص / الـCTA / الزاوية) فور توفر نتائج. لا يوجد تحليل بالذكاء الاصطناعي يعمل من هذه الصفحة."
              />
              {analysisSummary !== null && analysisSummary.failedJobsCount > 0 ? (
                <p className="tiny" role="alert" style={{ color: 'var(--rose)', margin: 0 }}>
                  محاولات فاشلة سابقة: {analysisSummary.failedJobsCount} — لم تُنتج نتائج بعد.
                </p>
              ) : null}
              <button type="button" className="ci-analyze-btn" onClick={handleRunAnalysis}>
                تشغيل التحليل
              </button>
              {analyzeNotice ? (
                <p className="ci-analyze-notice" role="status">
                  {analyzeNotice}
                </p>
              ) : null}
            </div>
          )}
        </section>
      ) : null}

      {/* Stage 5 — strategy selection (Task 224). LOCKED until creative analysis
          exists: strategy comes AFTER analysis results, not after performance alone. */}
      {activeStage === 'strategy' ? (
        <section className="ci-stage" aria-labelledby="ci-stage-5">
          <h2 className="ci-stage-title" id="ci-stage-5">
            <span className="ci-stage-num">5</span> اختيار الاستراتيجية
          </h2>
          {analysisReady ? (
            <StrategyBoard
              candidates={strategyCandidates}
              selectedId={selectedStrategyId}
              onSelect={setSelectedStrategyId}
            />
          ) : (
            <EmptyState
              title="تُفتح خيارات الاستراتيجية بعد تشغيل تحليل الإبداع."
              description="الاستراتيجية تُبنى على نتائج تحليل المحتوى وليس على الأداء وحده — أكمل المرحلة 4 أولاً."
            />
          )}

          {/* Task 259 — Strategy Rules derive/review lives HERE (the workflow
              owns it; the Strategy page is read-only/status). Pick an existing
              ACTIVE strategy/version, then derive rules from Creative Analysis
              + performance evidence via the live derive-strategy-rules edge
              function. View = strategy.rule.review; derive/approve/dismiss =
              leadership (backend re-checks).

              DELIBERATELY gated on the permission ONLY — NOT on analysisReady
              (operator-accepted, Task 259; analysisReady is REAL since Task
              260): the manage surface must stay reachable even when a client
              has zero analyses yet — the Strategy page is read-only, so hiding
              this section would make derive/approve/dismiss unreachable for
              that client. The step sits INSIDE stage 5 (after the Analysis
              stage), and the EF derives from whatever analyses actually exist
              — the summary reports the real evidence counts. */}
          {canViewStrategyRules ? (
            <div className="stack-sm" style={{ marginTop: 16 }}>
              <h3 style={{ margin: 0 }}>قواعد الاستراتيجية — اختر الاستراتيجية ثم اشتق</h3>
              {clientStrategiesState.status === 'loading' ? (
                <p className="muted" style={{ margin: 0 }}>
                  جاري تحميل استراتيجيات العميل…
                </p>
              ) : clientStrategiesState.status === 'error' ? (
                <p className="tiny" role="alert" style={{ color: 'var(--rose)', margin: 0 }}>
                  تعذّر تحميل استراتيجيات العميل.
                </p>
              ) : clientStrategiesState.status === 'ready' &&
                clientStrategiesState.strategies.length === 0 ? (
                <EmptyState
                  title="لا توجد استراتيجية نشطة بنسخة معتمدة"
                  description="أنشئ/فعّل استراتيجية بنسخة نشطة من صفحة الاستراتيجية أولاً، ثم اشتق القواعد من هنا."
                />
              ) : clientStrategiesState.status === 'ready' ? (
                <>
                  <label className="tiny" style={{ display: 'block' }}>
                    الاستراتيجية المستهدفة
                    <select
                      value={rulesStrategyId ?? ''}
                      onChange={(e) => {
                        setRulesStrategyId(e.target.value || null);
                      }}
                      style={{ display: 'block', marginTop: 4, maxWidth: 420 }}
                    >
                      {clientStrategiesState.strategies.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.title}
                          {s.is_baseline ? ' (أساسية)' : ''}
                        </option>
                      ))}
                    </select>
                  </label>
                  {rulesStrategyId && clientId ? (
                    <StrategyRulesPanel
                      clientId={clientId}
                      strategyId={rulesStrategyId}
                      variant="manage"
                      canManage={canManageStrategyRules}
                      canDerive={canManageStrategyRules}
                    />
                  ) : null}
                </>
              ) : null}
            </div>
          ) : null}
        </section>
      ) : null}
    </section>
  );
}

const STAGE_STATE_LABEL: Record<'available' | 'locked' | 'coming_soon', string> = {
  available: 'متاح',
  locked: 'مقفل',
  coming_soon: 'قريباً',
};

// Task 235 — clickable stepper that drives the stage switcher. Every stage is
// navigable (even locked / coming-soon ones) so its empty state can explain what
// is missing; the per-stage body renders the lock reason.
function WorkflowStepper({
  activeStage,
  onSelect,
  gate,
}: {
  readonly activeStage: CreativeWorkflowStageId;
  readonly onSelect: (id: CreativeWorkflowStageId) => void;
  readonly gate: CreativeWorkflowGateInputs;
}) {
  return (
    <ol className="ci-workflow-steps" aria-label="مراحل مسار ذكاء الإبداع">
      {CREATIVE_WORKFLOW_STAGES.map((s) => {
        const state = creativeWorkflowStageState(s.id, gate);
        const isActive = s.id === activeStage;
        const cls =
          'ci-workflow-step' +
          (isActive ? ' is-active' : '') +
          (state === 'locked' ? ' is-locked' : '') +
          (state === 'coming_soon' ? ' is-soon' : '');
        return (
          <li key={s.id} className={cls}>
            <button
              type="button"
              className="ci-workflow-step-btn"
              onClick={() => onSelect(s.id)}
              aria-current={isActive ? 'step' : undefined}
            >
              <span className="ci-workflow-step-num">{s.n}</span>
              <span className="ci-workflow-step-label">{s.label}</span>
              <span className="ci-workflow-step-state">
                {isActive ? 'الحالية' : STAGE_STATE_LABEL[state]}
              </span>
            </button>
          </li>
        );
      })}
    </ol>
  );
}

// Task 237 — Upload Creatives stage, LIVE. Bulk flow: classify → create CI
// intents (create-ci-asset-uploads) → probe dims + PUT each file to its signed
// URL → complete (complete-ci-asset-uploads). NO match is created (matching is
// the Task 222 flow in Match Review); NO AI / request-analysis.
type CiUploadStatus =
  | 'ready_to_upload'
  | 'unsupported_type'
  | 'uploading'
  | 'uploaded'
  | 'duplicate'
  | 'failed';

interface CiUploadItem {
  readonly key: string;
  readonly file: File;
  readonly normalized: string;
  readonly assetType: CiAssetUploadKind | null;
  readonly status: CiUploadStatus;
  readonly error?: string;
}

const CI_UPLOAD_STATUS_LABEL: Record<CiUploadStatus, string> = {
  ready_to_upload: 'جاهز للرفع',
  unsupported_type: 'نوع غير مدعوم',
  uploading: 'جارٍ الرفع…',
  uploaded: 'تم الرفع',
  duplicate: 'مكرر (موجود مسبقاً)',
  failed: 'فشل',
};

function classifyCiAssetType(mime: string): CiAssetUploadKind | null {
  const m = (mime || '').toLowerCase();
  if (m.startsWith('image/')) return 'image';
  if (m.startsWith('video/')) return 'video';
  return null;
}

// Browser-only dimension probe (transient ObjectURL, no network) — mirrors the
// ContentPage helper. Feeds width_px / height_px / duration_ms to complete.
async function probeCiMediaDimensions(
  file: File,
  kind: CiAssetUploadKind,
): Promise<{ width: number; height: number; durationMs: number | null }> {
  const url = URL.createObjectURL(file);
  try {
    if (kind === 'image') {
      return await new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
          if (img.naturalWidth <= 0 || img.naturalHeight <= 0) {
            reject(new Error('image has invalid dimensions'));
            return;
          }
          resolve({ width: img.naturalWidth, height: img.naturalHeight, durationMs: null });
        };
        img.onerror = () => reject(new Error('image failed to load'));
        img.src = url;
      });
    }
    return await new Promise((resolve, reject) => {
      const video = document.createElement('video');
      video.preload = 'metadata';
      video.onloadedmetadata = () => {
        if (video.videoWidth <= 0 || video.videoHeight <= 0) {
          reject(new Error('video has invalid dimensions'));
          return;
        }
        const ms = Number.isFinite(video.duration)
          ? Math.max(1, Math.round(video.duration * 1000))
          : 1;
        resolve({ width: video.videoWidth, height: video.videoHeight, durationMs: ms });
      };
      video.onerror = () => reject(new Error('video failed to load'));
      video.src = url;
    });
  } finally {
    URL.revokeObjectURL(url);
  }
}

function UploadCreativesStage({
  clientId,
  onUploaded,
  onGoToMatch,
}: {
  readonly clientId: string;
  readonly onUploaded: () => void;
  readonly onGoToMatch: () => void;
}) {
  const [items, setItems] = useState<ReadonlyArray<CiUploadItem>>([]);
  const [busy, setBusy] = useState(false);
  const [batchError, setBatchError] = useState<string | null>(null);
  const [didUpload, setDidUpload] = useState(false);

  function patch(key: string, next: Partial<CiUploadItem>) {
    setItems((prev) => prev.map((it) => (it.key === key ? { ...it, ...next } : it)));
  }

  function handlePick(files: FileList | null) {
    const picked = Array.from(files ?? []);
    setBatchError(null);
    setDidUpload(false);
    setItems(
      picked.map((file, i) => {
        const assetType = classifyCiAssetType(file.type);
        return {
          key: `${file.name}-${file.size}-${file.lastModified}-${i}`,
          file,
          normalized: normalizeCreativeFilename(file.name),
          assetType,
          status: assetType === null ? 'unsupported_type' : 'ready_to_upload',
        };
      }),
    );
  }

  async function handleUpload() {
    if (busy) return;
    const uploadable = items.filter(
      (it) => it.assetType !== null && it.status === 'ready_to_upload',
    );
    if (uploadable.length === 0) return;
    setBusy(true);
    setBatchError(null);
    setItems((prev) =>
      prev.map((it) =>
        uploadable.some((u) => u.key === it.key) ? { ...it, status: 'uploading' } : it,
      ),
    );

    try {
      // 1. Bulk prepare — reserve a CI upload intent + signed PUT URL per file.
      const created = await createCiAssetUploads({
        client_id: clientId,
        files: uploadable.map((it) => ({
          filename: it.file.name,
          asset_type: it.assetType as CiAssetUploadKind,
          mime_type: it.file.type.toLowerCase(),
          byte_size: it.file.size,
        })),
      });

      // 2. Per ready file: probe dims, PUT bytes to the signed URL, queue complete.
      //    The EF returns results in the SAME ORDER as the files we sent.
      const completeItems: CompleteCiAssetUploadsItem[] = [];
      const intentToKey = new Map<string, string>();
      for (let i = 0; i < created.results.length; i += 1) {
        const res = created.results[i];
        const item = uploadable[i];
        if (!item || !res) continue;
        if (res.status !== 'ready_to_upload' || !res.upload_intent_id || !res.upload_url) {
          patch(item.key, {
            status: res.status === 'unsupported_type' ? 'unsupported_type' : 'failed',
            error: res.error,
          });
          continue;
        }
        try {
          const dims = await probeCiMediaDimensions(item.file, item.assetType as CiAssetUploadKind);
          await putToSignedUploadUrl({
            url: res.upload_url,
            file: item.file,
            contentType: item.file.type.toLowerCase(),
          });
          intentToKey.set(res.upload_intent_id, item.key);
          completeItems.push({
            upload_intent_id: res.upload_intent_id,
            canonical_name: item.file.name,
            width_px: dims.width,
            height_px: dims.height,
            duration_ms: dims.durationMs,
          });
        } catch {
          patch(item.key, { status: 'failed', error: 'upload_put_failed' });
        }
      }

      // 3. Bulk complete — creates the creative_assets rows (NO match). Map the
      //    per-item status (uploaded | duplicate | failed) back to each file.
      let anyLanded = false;
      if (completeItems.length > 0) {
        const done = await completeCiAssetUploads({ items: completeItems });
        const returned = new Set<string>();
        done.results.forEach((r) => {
          returned.add(r.upload_intent_id);
          const key = intentToKey.get(r.upload_intent_id);
          if (key) patch(key, { status: r.status, error: r.error });
          if (r.status === 'uploaded' || r.status === 'duplicate') anyLanded = true;
        });
        intentToKey.forEach((key, intentId) => {
          if (!returned.has(intentId)) patch(key, { status: 'failed', error: 'no_result' });
        });
      }

      // 4. If anything landed, refresh the page's asset/group data and guide on.
      if (anyLanded) {
        setDidUpload(true);
        onUploaded();
      }
    } catch {
      setItems((prev) =>
        prev.map((it) =>
          it.status === 'uploading' ? { ...it, status: 'failed', error: 'batch_failed' } : it,
        ),
      );
      setBatchError('تعذّر رفع الدفعة — تأكد من الصلاحية والاتصال، ثم أعد المحاولة.');
    } finally {
      setBusy(false);
    }
  }

  const readyCount = items.filter((it) => it.status === 'ready_to_upload').length;

  return (
    <section className="ci-stage" aria-labelledby="ci-stage-2">
      <h2 className="ci-stage-title" id="ci-stage-2">
        <span className="ci-stage-num">2</span> رفع الإبداعات
      </h2>

      <p className="page-subtitle">
        ارفع صور/فيديو الإبداع دفعةً واحدة. لا يتم إنشاء أي مطابقة عند الرفع — بعد الرفع انتقل إلى
        «مطابقة الأصول» لربط كل إبداع بإعلاناته. لا يوجد تحليل بالذكاء الاصطناعي في هذه المرحلة.
      </p>

      <div className="ci-upload-dropzone">
        <p className="ci-upload-hint">اسحب صور/فيديو الإبداع هنا، أو اخترها للرفع</p>
        <p className="ci-upload-constraints">صور (JPG/PNG/WebP) وفيديو (MP4) — حتى 50 ملفاً</p>
        <label className="ci-upload-pick">
          اختيار ملفات
          <input
            type="file"
            multiple
            accept="image/*,video/*"
            disabled={busy}
            onChange={(e) => handlePick(e.target.files)}
          />
        </label>
      </div>

      {batchError ? (
        <p className="ci-link-error" role="alert">
          {batchError}
        </p>
      ) : null}

      {items.length > 0 ? (
        <table className="ci-upload-list ci-match-table">
          <thead>
            <tr>
              <th>الملف</th>
              <th>الاسم المُطبّع (للمطابقة)</th>
              <th>الحالة</th>
            </tr>
          </thead>
          <tbody>
            {items.map((it) => (
              <tr key={it.key}>
                <td>{it.file.name}</td>
                <td>{it.normalized}</td>
                <td>
                  <span className={`ci-badge ci-upload-status-${it.status}`}>
                    {CI_UPLOAD_STATUS_LABEL[it.status]}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <EmptyState
          title="لا توجد ملفات محددة بعد."
          description="اختر صور/فيديو الإبداع لرفعها؛ ستظهر هنا حالة كل ملف (جاهز / تم الرفع / مكرر / نوع غير مدعوم / فشل)."
        />
      )}

      <div className="ci-upload-actions">
        <button
          type="button"
          className="ci-analyze-btn"
          onClick={handleUpload}
          disabled={busy || readyCount === 0}
        >
          {busy ? 'جارٍ الرفع…' : `رفع الملفات${readyCount > 0 ? ` (${readyCount})` : ''}`}
        </button>
        {didUpload ? (
          <button type="button" className="auth-cta" onClick={onGoToMatch}>
            انتقل إلى مطابقة الأصول ←
          </button>
        ) : null}
      </div>

      {didUpload ? (
        <p className="ci-upload-done" role="status">
          تم رفع الإبداعات. الخطوة التالية: «مطابقة الأصول» لربطها بالإعلانات (لا تتم المطابقة
          تلقائياً).
        </p>
      ) : null}
    </section>
  );
}

function StrategyBoard({
  candidates,
  selectedId,
  onSelect,
}: {
  readonly candidates: ReadonlyArray<CreativeStrategyCandidate>;
  readonly selectedId: string | null;
  readonly onSelect: (id: string) => void;
}) {
  // Resolve the selected candidate from the CURRENT list, so a stale id (after a
  // date change / link refresh) collapses to "nothing selected" rather than a
  // dangling summary.
  const selected = candidates.find((c) => c.id === selectedId) ?? null;

  return (
    <section className="ci-strategy-board">
      <header className="ci-strategy-head">
        <h2>لوحة القرار — مرشّحات الاستراتيجية</h2>
        <p className="page-subtitle">
          مرشّحات حتمية مبنية على إشارات الأداء الحالية فقط (بدون ذكاء اصطناعي). راجِع وقارن، ثم
          اختر استراتيجية واحدة — لا يتم إنشاء أي محتوى أو مهمة أو موافقة تلقائياً.
        </p>
      </header>

      {candidates.length === 0 ? (
        <EmptyState
          title="لا توجد مرشّحات استراتيجية."
          description="الأداء مستقر أو البيانات غير كافية في هذه الفترة."
        />
      ) : (
        <div className="ci-strategy-cards">
          {candidates.map((c) => {
            const isSelected = c.id === selectedId;
            return (
              <article
                key={c.id}
                className={`ci-strategy-card${isSelected ? ' ci-strategy-card-selected' : ''}`}
              >
                <header className="ci-strategy-card-head">
                  <h3>{STRATEGY_KIND_LABEL[c.kind]}</h3>
                  <span className={`ci-badge ci-confidence-${c.confidence}`}>
                    {STRATEGY_CONFIDENCE_LABEL[c.confidence]}
                  </span>
                </header>
                <p className="ci-strategy-group">
                  من المجموعة: <strong>{c.group_display_name}</strong>
                </p>
                <p className="ci-strategy-platforms">
                  المنصات الداعمة:{' '}
                  {c.supporting_platforms.length > 0
                    ? c.supporting_platforms.map((p) => PLATFORM_LABEL[p]).join('، ')
                    : '—'}
                </p>
                {c.evidence.length > 0 ? (
                  <ul className="ci-strategy-evidence">
                    {c.evidence.map((e, i) => (
                      <li key={`${c.id}-ev-${i}`}>{e}</li>
                    ))}
                  </ul>
                ) : null}
                <p className="ci-strategy-next">
                  <strong>الخطوة التالية:</strong> {STRATEGY_NEXT_MOVE[c.kind]}
                </p>
                <button
                  type="button"
                  className={isSelected ? 'auth-cta' : 'btn-text'}
                  onClick={() => onSelect(c.id)}
                  aria-pressed={isSelected}
                >
                  {isSelected ? 'محدّدة ✓' : 'اختر هذه الاستراتيجية'}
                </button>
              </article>
            );
          })}
        </div>
      )}

      {selected ? (
        <div className="ci-strategy-summary card" role="status">
          <h3>الاستراتيجية المختارة</h3>
          <p>
            <strong>{STRATEGY_KIND_LABEL[selected.kind]}</strong> — {selected.group_display_name} (
            {STRATEGY_CONFIDENCE_LABEL[selected.confidence]})
          </p>
          <p>{STRATEGY_NEXT_MOVE[selected.kind]}</p>
          <p className="ci-strategy-note">
            اختيار بشري فقط — لا يُنشئ هذا الاختيار محتوى أو مهمة أو موافقة أو عنصر إنتاج. حفظ
            القرار بشكل دائم مرحلة لاحقة في هذا المسار.
          </p>
        </div>
      ) : null}
    </section>
  );
}

function AssetMatchReview({
  data,
  ignored,
  linkingAdId,
  onLink,
  onIgnore,
}: {
  readonly data: SuggestCreativeAssetPaidMatchesData;
  readonly ignored: ReadonlySet<string>;
  readonly linkingAdId: string | null;
  readonly onLink: (adId: string) => void;
  readonly onIgnore: (adId: string) => void;
}) {
  const visibleSuggestions = data.suggestions.filter((s) => !ignored.has(s.ad_id));

  return (
    <div className="ci-review">
      <p className="ci-review-asset">
        الأصل: <strong>{data.asset.canonical_name}</strong>
        {data.partial ? ' — عرض جزئي للفترة' : ''}
      </p>

      <ul className="ci-review-platforms">
        {(['meta_ads', 'snapchat_ads', 'tiktok_ads'] as const).map((p) => (
          <li key={p}>
            {PLATFORM_LABEL[p]}: {CONNECTION_LABEL[data.platforms[p]]}
          </li>
        ))}
      </ul>

      <h3>إعلانات مرتبطة بالفعل</h3>
      {data.existing_matches.length === 0 ? (
        <EmptyState title="لا توجد روابط نشطة بعد." />
      ) : (
        <table className="ci-match-table">
          <thead>
            <tr>
              <th>الإعلان</th>
              <th>المنصة</th>
              <th>الطريقة</th>
              <th>الثقة</th>
            </tr>
          </thead>
          <tbody>
            {data.existing_matches.map((m) => (
              <tr key={m.id}>
                <td>{m.ad_name}</td>
                <td>{m.platform ? PLATFORM_LABEL[m.platform] : '—'}</td>
                <td>{MATCH_METHOD_LABEL[m.match_method]}</td>
                <td>{fmtConfidence(m.match_confidence)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <h3>مطابقات مقترحة</h3>
      {visibleSuggestions.length === 0 ? (
        <EmptyState
          title="لا توجد مطابقات مقترحة."
          description="جرّب توسيع الفترة أو أضف أسماء بديلة للأصل."
        />
      ) : (
        <table className="ci-match-table">
          <thead>
            <tr>
              <th>الإعلان</th>
              <th>المنصة</th>
              <th>الطريقة</th>
              <th>الثقة</th>
              <th>إجراء</th>
            </tr>
          </thead>
          <tbody>
            {visibleSuggestions.map((s) => (
              <tr key={s.ad_id}>
                <td>{s.ad_name}</td>
                <td>{PLATFORM_LABEL[s.platform]}</td>
                <td>{PAID_METHOD_LABEL[s.match_method]}</td>
                <td>{fmtConfidence(s.confidence)}</td>
                <td className="ci-match-actions">
                  <button
                    type="button"
                    onClick={() => onLink(s.ad_id)}
                    disabled={linkingAdId !== null}
                  >
                    {linkingAdId === s.ad_id ? 'جارٍ الربط…' : 'ربط'}
                  </button>
                  <button
                    type="button"
                    className="ci-ignore"
                    onClick={() => onIgnore(s.ad_id)}
                    disabled={linkingAdId !== null}
                  >
                    تجاهل
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <h3>إعلانات غير مطابقة</h3>
      {data.unmatched.length === 0 ? (
        <EmptyState title="لا توجد إعلانات غير مطابقة في هذه الفترة." />
      ) : (
        <table className="ci-match-table">
          <thead>
            <tr>
              <th>الإعلان</th>
              <th>المنصة</th>
              <th>إجراء</th>
            </tr>
          </thead>
          <tbody>
            {data.unmatched.map((u) => (
              <tr key={u.ad_id}>
                <td>{u.ad_name}</td>
                <td>{PLATFORM_LABEL[u.platform]}</td>
                <td className="ci-match-actions">
                  <button
                    type="button"
                    onClick={() => onLink(u.ad_id)}
                    disabled={linkingAdId !== null}
                  >
                    {linkingAdId === u.ad_id ? 'جارٍ الربط…' : 'ربط يدوي'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <p className="ci-review-note">
        «تجاهل» يخفي الاقتراح في هذه الجلسة فقط (بدون أي تغيير دائم). فكّ الربط الدائم لرابط قائم
        إجراء لاحق منفصل.
      </p>
    </div>
  );
}

function ContentGroupCard({ group }: { readonly group: CreativeIntelligenceContentGroup }) {
  return (
    <article className="ci-group-card">
      <header className="ci-group-head">
        <h2>{group.display_name}</h2>
        <span className={`ci-badge ci-badge-${group.match_quality}`}>{group.match_quality}</span>
      </header>

      <table className="ci-platform-table">
        <thead>
          <tr>
            <th>المنصة</th>
            <th>الحالة</th>
            <th>الإنفاق</th>
            <th>الظهور</th>
            <th>CTR</th>
            <th>ROAS</th>
            <th>الأداء</th>
            <th>الإجهاد</th>
          </tr>
        </thead>
        <tbody>
          {group.platforms.map((p) => {
            const hasData = p.availability === 'has_data';
            const best = group.aggregate.best_platform === p.platform;
            const worst = group.aggregate.worst_platform === p.platform;
            return (
              <tr key={p.platform} className={best ? 'ci-best' : worst ? 'ci-worst' : undefined}>
                <td>
                  {PLATFORM_LABEL[p.platform]}
                  {best ? ' 🏆' : ''}
                </td>
                <td>
                  {hasData ? `${p.matched_ads_count} إعلان` : AVAILABILITY_LABEL[p.availability]}
                </td>
                <td>{hasData ? fmtMoney(p.spend) : '—'}</td>
                <td>{hasData ? fmtNum(p.impressions) : '—'}</td>
                <td>{hasData ? fmtPct(p.ctr) : '—'}</td>
                <td>{hasData ? fmtRatio(p.roas) : '—'}</td>
                <td>{hasData ? PERF_LABEL[p.performance_signal] : '—'}</td>
                <td>{hasData ? FATIGUE_LABEL[p.fatigue_signal] : '—'}</td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {group.recommendations.length > 0 ? (
        <ul className="ci-recommendations">
          {group.recommendations.map((r, i) => (
            <li key={`${r.kind}-${i}`}>
              <strong>{RECO_LABEL[r.kind]}</strong> — {r.reason}
            </li>
          ))}
        </ul>
      ) : null}

      <p className="ci-next-step">
        الخطوة التالية المقفلة: إنشاء دفعة محتوى ← موافقة العميل ← الإنتاج (في مهمة لاحقة).
      </p>
    </article>
  );
}
